import { makeAutoObservable, runInAction } from 'mobx'
import adminClient from './adminClient'
import { matchesImpersonationTarget } from './impersonationTarget'

class AdminStore {
	session = null
	impersonation = null
	impersonationEpoch = 0
	impersonationInvalidation = null
	pendingIntent = null
	loading = false
	error = null

	constructor(client = adminClient) {
		this.client = client
		makeAutoObservable(this, { client: false })
	}

	get elevated() { return Boolean(this.session && this.client.hasAdminSession()) }
	get impersonating() { return Boolean(this.impersonation && this.client.hasImpersonationSession()) }
	get hasPendingIntent() { return this.pendingIntent?.action === 'startImpersonation' }

	async elevate(password) {
		this.loading = true; this.error = null
		try {
			const session = await this.client.elevate(password)
			runInAction(() => { this.session = session })
			return session
		} catch (error) {
			runInAction(() => { this.error = error.response?.data?.error?.code || error.code || 'ADMIN_ERROR' })
			throw error
		} finally { runInAction(() => { this.loading = false }) }
	}

	async elevateAndContinue(password) {
		await this.elevate(password)
		return this.continuePendingIntent()
	}

	async logout() {
		await this.client.logout().catch(() => null)
		runInAction(() => { this.session = null; this.impersonation = null; this.pendingIntent = null; this.error = null })
	}

	async requestImpersonation(target, reason) {
		if (!target?.effectiveUserId || !target?.organizationId || !target?.membershipId) {
			const error = Object.assign(new Error('An exact active membership target is required'), { code: 'ADMIN_INVALID_TARGET' })
			runInAction(() => { this.error = error.code })
			throw error
		}
		runInAction(() => {
			this.pendingIntent = { action: 'startImpersonation', target: { ...target }, reason }
			this.error = null
		})
		return this.continuePendingIntent()
	}

	async continuePendingIntent() {
		const intent = this.pendingIntent
		if (!intent) return { started: false, requiresElevation: false }
		if (!this.client.hasAdminSession()) return { started: false, requiresElevation: true }
		try {
			const result = await this.client.post('/api/admin/impersonation', {
				effectiveUserId: intent.target.effectiveUserId,
				organizationId: intent.target.organizationId,
				reason: intent.reason,
			})
			if (!matchesImpersonationTarget(result, intent.target)) {
				throw Object.assign(new Error('Impersonation response does not match selected tuple'), { code: 'ADMIN_IMPERSONATION_TUPLE_MISMATCH' })
			}
			this.client.setImpersonationToken(result.token)
			const safeImpersonation = { ...result }
			delete safeImpersonation.token
			runInAction(() => {
				this.impersonation = safeImpersonation
				this.impersonationEpoch += 1
				this.impersonationInvalidation = null
				this.pendingIntent = null
				this.error = null
			})
			return { started: true, requiresElevation: false, impersonation: safeImpersonation }
		} catch (error) {
			const code = error.response?.data?.error?.code || error.code || 'ADMIN_ERROR'
			const requiresElevation = code === 'ADMIN_AUTH_REQUIRED' || code === 'ADMIN_SESSION_EXPIRED'
			this.client.clearImpersonationSession()
			runInAction(() => {
				this.impersonation = null
				this.impersonationEpoch += 1
				this.error = code
				if (!requiresElevation) this.pendingIntent = null
			})
			if (requiresElevation) return { started: false, requiresElevation: true }
			throw error
		}
	}

	async endImpersonation() {
		await this.client.endImpersonation().catch(() => null)
		runInAction(() => { this.impersonation = null; this.pendingIntent = null; this.impersonationEpoch += 1; this.impersonationInvalidation = null })
	}

	invalidateImpersonation(code = 'ADMIN_SESSION_EXPIRED') {
		this.client.clearImpersonationSession()
		this.impersonation = null
		this.pendingIntent = null
		this.impersonationEpoch += 1
		this.impersonationInvalidation = code
	}

	clear() {
		this.client.clear()
		this.session = null
		this.impersonation = null
		this.impersonationEpoch += 1
		this.impersonationInvalidation = null
		this.pendingIntent = null
		this.error = null
	}
}

const adminStore = new AdminStore()
export { AdminStore }
export default adminStore
